import { UseMutationResult, useMutation, useQueryClient } from '@tanstack/react-query';
import { ImpersonationBroker, asApiError } from '../../../extViewmodels';
import { useUserSession } from '../../../models/hooks/appStateHooks';
import { queryKeys } from './queryKeys';

export type DeleteContactVariables = {
	companyId: string;
	contactId: string;
};

/**
 * Delete a contact via `DELETE contact/{contactId}` — the same endpoint the
 * legacy `ContactViewModel.delete()` uses. The lead's contacts are base
 * `Api.ContactViewModel` instances (see `leads/lead.ts` `deleteContact` →
 * `contact.delete()`), whose `getEntityApiPath()` returns `'contact'`. On
 * success, invalidates the contacts query so the row disappears from the
 * LeftColumn.
 */
export function useDeleteContact(): UseMutationResult<void, Error, DeleteContactVariables> {
	const userSession = useUserSession();
	const queryClient = useQueryClient();

	return useMutation<void, Error, DeleteContactVariables>({
		mutationFn: async ({ contactId }) => {
			const result = await userSession.webServiceHelper.callWebServiceAsync<void>(
				ImpersonationBroker.composeApiUrl({ urlPath: `contact/${contactId}` }),
				'DELETE'
			);
			if (!result.success) {
				throw asApiError(result);
			}
		},
		onSuccess: (_data, variables) => {
			queryClient.invalidateQueries({ queryKey: queryKeys.focusedView.contacts(variables.companyId) });
		},
	});
}
